import { Currency, ProfitabilityEstimateDTO, useApiPostDealFinanceV1ProfitabilityCalculatorCreate } from 'api';
import { useCallback } from 'react';

import type { NetTakeError, NetTakeRate } from '../../types';

type HookProps = {
  onSettled?: (data: ProfitabilityEstimateDTO | undefined, error: NetTakeError) => void;
};
export const useNetTakeRateCalculator = ({ onSettled }: HookProps) => {
  const {
    mutateAsync: calculateNTR,
    isPending: isCalculateNetTakeRateLoading,
    data: netTakeRate,
    error: netTakeRateError,
  } = useApiPostDealFinanceV1ProfitabilityCalculatorCreate({
    mutation: {
      onSettled: (data, error) => {
        onSettled?.(data, error);
      },
    },
  });

  const calculateNetTakeRate = useCallback(
    async ({
      sellerPricePerTonne,
      sellerCurrency,
      tonnesPerLoad,
      numberOfLoads,
      pernRevenuePerTonne,
      transportPricePerTonne,
      buyerPricePerTonne,
      buyerPriceAsIndexPercentage,
      buyerCurrency,
      sellerPriceAsIndexPercentage,
      materialId,
      transportCurrency,
    }: {
      sellerPricePerTonne?: number | string;
      sellerPriceAsIndexPercentage?: number;
      sellerCurrency: Currency;
      tonnesPerLoad: number;
      numberOfLoads: number;
      pernRevenuePerTonne: number;
      transportPricePerTonne?: number;
      transportCurrency: Currency;
      materialId: number;
      buyerPricePerTonne?: number | string;
      buyerPriceAsIndexPercentage?: number;
      buyerCurrency: Currency;
    }) => {
      const numericSellerPrice =
        typeof sellerPricePerTonne === 'string' ? parseFloat(sellerPricePerTonne) : sellerPricePerTonne;

      const numericBuyerPrice =
        typeof buyerPricePerTonne === 'string' ? parseFloat(buyerPricePerTonne) : buyerPricePerTonne;

      if ((!numericSellerPrice || numericSellerPrice <= 1) && !sellerPriceAsIndexPercentage) {
        return;
      }

      if ((!numericBuyerPrice || numericBuyerPrice <= 1) && !buyerPriceAsIndexPercentage) {
        return;
      }

      if (transportPricePerTonne === undefined) {
        return;
      }

      return calculateNTR({
        data: {
          buyer_agent_commission_currency: Currency.USD,
          buyer_agent_commission_per_tonne: '0',
          buyer_currency: buyerCurrency,
          buyer_price_as_index_percentage: buyerPriceAsIndexPercentage?.toString() || undefined,
          buyer_price_per_tonne: numericBuyerPrice ? numericBuyerPrice.toFixed(2).toString() : undefined,
          material: materialId,
          pern_currency: 'GBP',
          pern_revenue_per_tonne: pernRevenuePerTonne.toString(),
          seller_agent_commission_currency: Currency.USD,
          seller_agent_commission_per_tonne: '0',
          seller_currency: sellerCurrency,
          seller_price_as_index_percentage: sellerPriceAsIndexPercentage?.toString() || undefined,
          seller_price_per_tonne: numericSellerPrice ? numericSellerPrice.toFixed(2).toString() : undefined,
          shipping_cost_per_load: transportPricePerTonne.toString(),
          shipping_cost_per_load_currency: transportCurrency,
          tonnes: (tonnesPerLoad * numberOfLoads).toString(),
          tonnes_per_load: tonnesPerLoad.toString(),
        },
      });
    },
    [calculateNTR]
  );

  return {
    calculateNetTakeRate,
    netTakeRate: {
      error: netTakeRateError,
      isLoading: isCalculateNetTakeRateLoading,
      rate: Number(netTakeRate?.net_take_rate || 0),
    } satisfies NetTakeRate,
  };
};
